import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';

import { LoaderComponent } from './loader/loader.component';

@Component({
    selector: 'app-roulette-betting-timer',
    standalone: true,
    imports: [CommonModule, LoaderComponent],
    templateUrl: './betting-timer.component.html',
    styleUrl: './betting-timer.component.scss'
})
export class RouletteBettingTimerComponent implements OnChanges, OnDestroy {
    @Input() bettingWindowInSeconds!: number;
    @Input() preparationTimeInSeconds!: number;
    @Input() remainingBettingTimeInSeconds?: number;
    @Input() betsOpenAfterBallThrowInSeconds!: number;
    @Input() ballShootInTimeLeft?: number;
    @Input() isClosed = false;

    progress = 1;
    hurryUp = false;
    ballShootIn = false;

    private readonly smoothTransitionSpeed = 0.5;
    private readonly updateIntervalMs = 50;
    private readonly msPerSecond = 1000;

    private timerEndTime = 0;
    private currentPhaseStartTime = 0;
    private currentPhaseDuration = 0;
    private elapsedAtPhaseStart = 0;
    private intervalId?: ReturnType<typeof setInterval>;
    private targetProgress = 1;
    private isSmoothTransitioning = false;

    get totalArcTime(): number {
        return this.bettingWindowInSeconds + this.betsOpenAfterBallThrowInSeconds;
    }

    get gameBettingTimeInSeconds(): number {
        return this.bettingWindowInSeconds - this.preparationTimeInSeconds;
    }

    ngOnChanges(changes: SimpleChanges): void {
        // Handle new round start or page refresh
        if ('bettingWindowInSeconds' in changes || 'remainingBettingTimeInSeconds' in changes) {
            this.handleBettingPhaseStart();
        }

        // Handle ball shoot in phase (subPhase 1)
        if ('ballShootInTimeLeft' in changes && this.ballShootInTimeLeft !== undefined && this.ballShootInTimeLeft > 0) {
            this.handleBallShootInPhase();
        }
    }

    ngOnDestroy(): void {
        this.stopTimer();
    }

    private handleBettingPhaseStart(): void {
        // Use remainingBettingTime for page refresh, otherwise full betting window for new round
        const effectiveBettingTime = this.remainingBettingTimeInSeconds ?? this.bettingWindowInSeconds;

        if (effectiveBettingTime <= 0) {
            this.showBallShootInWaiting();
            return;
        }

        // Calculate elapsed time from start
        const elapsedFromStart = this.bettingWindowInSeconds - effectiveBettingTime;

        this.elapsedAtPhaseStart = elapsedFromStart;
        this.currentPhaseDuration = effectiveBettingTime;
        this.currentPhaseStartTime = Date.now();
        this.timerEndTime = this.currentPhaseStartTime + effectiveBettingTime * this.msPerSecond;

        // Set progress based on elapsed time
        this.progress = 1 - elapsedFromStart / this.totalArcTime;
        this.targetProgress = this.progress;
        this.isSmoothTransitioning = false;

        this.ballShootIn = false;
        this.updateHurryUpState(effectiveBettingTime);

        this.startTimer();
    }

    private showBallShootInWaiting(): void {
        // Betting time expired, waiting for ball to be shot - don't start timer yet
        this.stopTimer();

        this.ballShootIn = true;
        this.hurryUp = false;

        // Position at start of ball shoot in segment and wait
        this.progress = 1 - this.bettingWindowInSeconds / this.totalArcTime;
        this.targetProgress = this.progress;
        this.isSmoothTransitioning = false;
    }

    private handleBallShootInPhase(): void {
        if (this.ballShootInTimeLeft === undefined || this.ballShootInTimeLeft <= 0) {
            return;
        }

        const ballShootInTime = this.ballShootInTimeLeft;
        const timeLeftMs = ballShootInTime * this.msPerSecond;

        const formatTime = (date: Date): string => {
            return `${date.getHours()}h ${date.getMinutes()}m ${date.getSeconds()}s ${date.getMilliseconds()}ms`;
        };

        // eslint-disable-next-line no-console
        console.log('Ball shoot in: Animation started at', formatTime(new Date()), ', received timeLeft:', timeLeftMs, 'ms');

        setTimeout(() => {
            // eslint-disable-next-line no-console
            console.log('Ball shoot in: TimeLeft has finished', formatTime(new Date()), '(setTimeout after', timeLeftMs, 'ms)');
        }, timeLeftMs);

        // We're now in ball shoot in phase
        this.ballShootIn = true;
        this.hurryUp = false;

        // Calculate where we should be in the arc based on timeLeft received
        const targetElapsed = this.bettingWindowInSeconds + (this.betsOpenAfterBallThrowInSeconds - ballShootInTime);
        const newTargetProgress = 1 - targetElapsed / this.totalArcTime;

        this.targetProgress = newTargetProgress;
        this.isSmoothTransitioning = true;

        // Update phase tracking - elapsed is based on how much time has passed
        this.elapsedAtPhaseStart = targetElapsed;
        this.currentPhaseDuration = ballShootInTime;
        this.currentPhaseStartTime = Date.now();
        this.timerEndTime = this.currentPhaseStartTime + ballShootInTime * this.msPerSecond;

        this.startTimer();
    }

    private startTimer(): void {
        this.stopTimer();

        this.intervalId = setInterval(() => {
            this.updateProgress();
        }, this.updateIntervalMs);
    }

    private stopTimer(): void {
        if (this.intervalId) {
            clearInterval(this.intervalId);
            this.intervalId = undefined;
        }
    }

    private updateProgress(): void {
        const now = Date.now();

        if (now >= this.timerEndTime) {
            // eslint-disable-next-line no-console
            console.log('Ball shoot in: Waiting to throw ball');
            this.stopTimer();
            return;
        }

        const elapsedInPhase = (now - this.currentPhaseStartTime) / this.msPerSecond;
        const totalElapsed = this.elapsedAtPhaseStart + elapsedInPhase;
        const naturalProgress = 1 - totalElapsed / this.totalArcTime;

        if (this.isSmoothTransitioning) {
            this.targetProgress = naturalProgress;

            const transitionAmount = (this.smoothTransitionSpeed * this.updateIntervalMs) / this.msPerSecond;
            const progressDiff = this.targetProgress - this.progress;
            const progressThreshold = 0.01;
            const transitionMultiplier = 10;

            if (Math.abs(progressDiff) < progressThreshold) {
                this.progress = this.targetProgress;
                this.isSmoothTransitioning = false;
            } else {
                this.progress += progressDiff * transitionAmount * transitionMultiplier;
            }
        } else {
            this.progress = naturalProgress;
            this.targetProgress = naturalProgress;
        }

        // Update hurry up state during betting phase
        if (!this.ballShootIn) {
            const remainingBettingTime = this.currentPhaseDuration - elapsedInPhase;
            this.updateHurryUpState(remainingBettingTime);
        }
    }

    private updateHurryUpState(remainingBettingTime: number): void {
        this.hurryUp = remainingBettingTime <= this.preparationTimeInSeconds && remainingBettingTime > 0;
    }
}
